import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';

import { BlockDownload } from '../block-download';
import { BlockView } from '../block-view';
import { Block, BlockChild, BlockPage } from '../julia-block.model';
import { JuliaBlocksService } from '../julia-blocks.service';

/** A record Julia showed: its fields, then its lists as tabs, each a table paged by the server; the record opens in
 * the platform and is exported as `.xlsx` with one sheet per list. */
@Component({
  selector: 'app-julia-record-block',
  templateUrl: './julia-record-block.component.html',
  styleUrls: ['./julia-record-block.component.scss'],
})
export class JuliaRecordBlockComponent implements BlockView, OnInit {
  @Input() block!: Block;
  @Input() sessionId!: string;
  @Output() expired = new EventEmitter<void>();

  page: BlockPage | null = null;
  tab: BlockChild | null = null;
  exporting = false;
  error: string | null = null;

  constructor(private blocks: JuliaBlocksService, private router: Router) {}

  ngOnInit() {
    this.tab = this.children[0] ?? null;
    if (this.block.page) {
      this.page = this.block.page;
      return;
    }
    this.blocks.rows(this.sessionId, this.block.id, 1, 1, null).subscribe({
      next: (page) => this.page = page,
      error: (err: HttpErrorResponse) => this.error = this.errorKey(err),
    });
  }

  get children(): BlockChild[] {
    return this.block.children ?? [];
  }

  /** The record's non-empty fields, as titled in the block. */
  get fields(): { title: string; value: string }[] {
    const cells = this.page?.rows[0] ?? {};
    return (this.page?.columns ?? [])
      .map(title => ({ title, value: cells[title] }))
      .filter(field => field.value !== null && field.value !== undefined && field.value !== '')
      .map(field => ({ title: field.title, value: String(field.value) }));
  }

  get link(): string | null {
    return this.page?.links?.[0] ?? null;
  }

  open(event: MouseEvent, link: string) {
    event.preventDefault();
    this.router.navigateByUrl(link);
  }

  select(child: BlockChild) {
    this.tab = child;
  }

  download() {
    this.exporting = true;
    this.blocks.export(this.sessionId, this.block.id, null).subscribe({
      next: (response) => {
        BlockDownload.save(response, BlockDownload.fileName(this.block.alias));
        this.exporting = false;
      },
      error: (err: HttpErrorResponse) => {
        this.error = this.errorKey(err);
        this.exporting = false;
      },
    });
  }

  private errorKey(err: HttpErrorResponse): string {
    if (err.status === 410) {
      this.expired.emit();
      return 'julia.blocks.expired';
    }
    if (err.status === 404) return 'julia.blocks.not_found';
    return 'julia.blocks.error';
  }
}
